import React, { ChangeEvent, ChangeEventHandler, FC } from "react";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import FormText from "./FormText";
import { Controller, useFormContext } from "react-hook-form";
import ErrorMessage from "./ErrorMessage";
import Button from "../buttons/Button";

const FormDropzone: React.FC<{
  name: string;
  type: "CV" | "Video" | "Other";
  errorMessage?: string;
}> = ({ name, type, errorMessage }) => {
  const { t } = useTranslation("form");
  const accept = Object.fromEntries(
    Object.entries(
      type === "Video"
        ? {
            "video/mp4": [".mp4"],
            "video/mov": [".mov"],
          }
        : {
            "application/pdf": [".pdf"],
            "application/docx": [".docx"],            
          }
    ).filter(([_, value]) => value !== undefined)
  );

  const getFormText = (type: "CV" | "Video" | "Other") => {
    switch (type) {
      case "Video":
        return t("form:dragAndDropVideo");
      case "Other":
        return t("form:dragAndDropOther");
      default:
        return t("form:dragAndDropCV");
    }
  };

  const { control, formState, watch, resetField } = useFormContext();
  const multiple = false;

  const removeFile = (event: React.MouseEvent<HTMLButtonElement>) => {
    resetField(name);
  };

  const field = watch(name);
  const hasError = !!formState.errors?.[name];
  const errorMsg = hasError
    ? t(formState.errors?.[name]?.message as string)
    : undefined;

  return (
    <Controller
      render={({ field: { onChange } }) => (
        <Dropzone
          text={getFormText(type)}
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            const file = multiple
              ? e.target.files
              : e.target.files?.[0] ?? null;
            onChange(file);
          }}
          errorMessage={errorMsg}
          onHandleClear={removeFile}
          isEmpty={!field}
        />
      )}
      name={name}
      control={control}
      defaultValue=""
    />
  );
};

const createFileList = (files: File[]): FileList => {
  const dataTransfer = new DataTransfer();
  files.forEach((file) => dataTransfer.items.add(file));
  return dataTransfer.files;
};

const Dropzone: FC<{
  text: string;
  multiple?: boolean;
  accept?: { [key: string]: string[] };
  onChange?: ChangeEventHandler<HTMLInputElement>;
  errorMessage?: string;
  onHandleClear: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isEmpty?: boolean;
}> = ({
  text,
  multiple,
  accept,
  onChange,
  errorMessage,
  onHandleClear,
  isEmpty,
  ...rest
}) => {
  const { t } = useTranslation("form");
  const { getRootProps, getInputProps, isDragActive, acceptedFiles } =
    useDropzone({
      accept,
      multiple,
      onDrop(acceptedFiles) {
        const event = {
          target: {
            files: createFileList(acceptedFiles),
          },
        } as unknown as ChangeEvent<HTMLInputElement>;
        onChange && onChange(event);
      },
      ...rest,
    });

  const internalFile = acceptedFiles.map((file) => (
    <FormText bold key={file.name} text={file.name} />
  ))[0];

  const hasFile = internalFile && !isEmpty;

  const backgroundStyling = isDragActive ? "bg-gray-100" : "";
  const borderStyling = hasFile ? "" : " border-dashed";

  return (
    <div className="relative">
      <section
        {...getRootProps()}
        className={`min-h-32 border bg-pitchlerGray ${borderStyling}${backgroundStyling} focus-ring:border-primary hover:bg-pitchlerGrayHover hover:cursor-pointer rounded-l-[24px] rounded-tr-[24px] flex justify-center items-center text-center`}
      >
        <div className="m-3">
          <input {...getInputProps({ onChange })} />
          {hasFile ? (
            <div>
              {internalFile}
              <div className="mt-3">
                <FormText text={t("form:clickToSwitch")} />
              </div>
            </div>
          ) : (
            <FormText
              text={isDragActive ? t("dropHere") : text}
              color={"text-gray-500"}
            />
          )}
        </div>
      </section>
      {errorMessage && (
        <div className="mt-1">
          <ErrorMessage message={errorMessage} />
        </div>
      )}
      {hasFile && (
        <div className="absolute top-0 right-0 p-2">
          <Button
            onClick={onHandleClear}
            icon={
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12.0022 1.25H12.0662C12.559 1.24999 12.9773 1.24997 13.325 1.28107C13.6941 1.31408 14.0382 1.38555 14.3724 1.55897C14.5045 1.6275 14.6308 1.70664 14.7501 1.79561C15.052 2.02074 15.2664 2.29917 15.4571 2.61688C15.6367 2.91618 15.8192 3.29261 16.0341 3.73608L16.5256 4.75H19.4931C19.4972 4.74997 19.5013 4.74997 19.5054 4.75H21C21.4142 4.75 21.75 5.08579 21.75 5.5C21.75 5.91422 21.4142 6.25 21 6.25H20.2051L19.6265 15.61C19.5493 16.8579 19.4881 17.8482 19.3626 18.6392C19.2339 19.4504 19.028 20.126 18.616 20.7168C18.239 21.2574 17.7537 21.7137 17.1909 22.0565C16.5757 22.4313 15.8888 22.5952 15.0712 22.6736C14.2739 22.75 13.2818 22.75 12.0315 22.75H11.9539C10.702 22.75 9.70857 22.75 8.91038 22.6734C8.09183 22.5949 7.40414 22.4307 6.78852 22.0552C6.22534 21.7117 5.73989 21.2547 5.36308 20.7133C4.95119 20.1214 4.74585 19.4448 4.61813 18.6325C4.4936 17.8404 4.43374 16.8487 4.35831 15.5991L3.79391 6.25H3C2.58579 6.25 2.25 5.91422 2.25 5.5C2.25 5.08579 2.58579 4.75 3 4.75H4.49469C4.4987 4.74997 4.50271 4.74997 4.50671 4.75H7.57055L7.98957 3.83076C8.19914 3.37097 8.37691 2.98096 8.55391 2.67073C8.74173 2.34153 8.95528 2.05248 9.26078 1.81811C9.38136 1.72561 9.50939 1.64326 9.64357 1.57192C9.98355 1.39114 10.3351 1.31672 10.7126 1.28235C11.0683 1.24997 11.4969 1.24999 12.0022 1.25ZM9.21904 4.75H14.8587L14.6982 4.41888C14.4657 3.93926 14.312 3.62382 14.1709 3.38884C14.0371 3.16588 13.9426 3.06458 13.8533 2.99801C13.7991 2.95756 13.7417 2.92159 13.6816 2.89044C13.5828 2.83916 13.4504 2.79827 13.1914 2.77511C12.9184 2.75069 12.5675 2.75 12.0345 2.75C11.4882 2.75 11.1282 2.75072 10.8486 2.77618C10.5831 2.80035 10.4481 2.84298 10.3478 2.89633C10.2868 2.92876 10.2286 2.96619 10.1738 3.00823C10.0837 3.07739 9.98887 3.18253 9.85677 3.41407C9.7176 3.65798 9.56765 3.98523 9.34105 4.48234L9.21904 4.75ZM5.29664 6.25L5.85324 15.47C5.93155 16.767 5.98788 17.6868 6.09993 18.3995C6.2101 19.1002 6.36437 19.5261 6.59429 19.8564C6.8521 20.2269 7.18426 20.5396 7.56959 20.7746C7.91322 20.9842 8.34755 21.1126 9.05363 21.1803C9.77183 21.2492 10.6933 21.25 11.9927 21.25C13.2904 21.25 14.2107 21.2492 14.928 21.1804C15.6332 21.1128 16.0671 20.9847 16.4105 20.7755C16.7956 20.5409 17.1276 20.2288 17.3855 19.8589C17.6156 19.529 17.7702 19.1039 17.8812 18.4042C17.9941 17.6925 18.0516 16.774 18.1317 15.4788L18.7022 6.25H5.29664ZM9.5 9.75C9.91421 9.75 10.25 10.0858 10.25 10.5L10.25 16.5C10.25 16.9142 9.91421 17.25 9.5 17.25C9.08579 17.25 8.75 16.9142 8.75 16.5L8.75 10.5C8.75 10.0858 9.08579 9.75 9.5 9.75ZM14.5 9.75C14.9142 9.75 15.25 10.0858 15.25 10.5V16.5C15.25 16.9142 14.9142 17.25 14.5 17.25C14.0858 17.25 13.75 16.9142 13.75 16.5V10.5C13.75 10.0858 14.0858 9.75 14.5 9.75Z"
                  fill="currentColor"
                />
              </svg>
            }
          />
        </div>
      )}
    </div>
  );
};

export default FormDropzone;
